import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SchemaOrg, { generateBreadcrumbSchema } from '@/components/feature/SchemaOrg';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const articles = slug ? getArticlesByCategory(slug) : [];

  if (!category) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-[60vh] flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-3">
              分类未找到
            </h1>
            <p className="text-foreground-500 text-sm mb-4">
              请检查分类链接是否正确
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-lg text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: `category:${slug}` },
  ];

  return (
    <>
      <ScrollToTop />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          items: [
            { name: '首页', url: 'https://ntrinsight.github.io/' },
            { name: category.name, url: `https://ntrinsight.github.io/category/${slug}` },
          ],
        }}
      />

      <Navbar />

      <main className="pt-16">
        <Breadcrumb items={breadcrumbItems} />

        {/* Header */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-50 text-primary-600">
                <i className={`${category.icon} text-xl`}></i>
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  {category.name}
                </h1>
                <p className="text-foreground-600 text-sm mt-1">
                  {category.description}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="w-full px-4 md:px-6 py-6 bg-background-50 border-b border-background-200">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center gap-2 overflow-x-auto pb-1">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Articles Grid */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-6xl mx-auto">
            {articles.length > 0 ? (
              <>
                <p className="text-sm text-foreground-500 mb-6">
                  共 {articles.length} 篇文章
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {articles.map((article) => (
                    <ArticleCard key={article.slug} {...article} />
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-file-list-3-line text-2xl text-foreground-400"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-800 mb-2">
                  暂无文章
                </h3>
                <p className="text-sm text-foreground-500">
                  该分类下暂时没有文章，请稍后再来
                </p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}